import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { requestEmailVerification, confirmEmail, checkVerificationStatus, getUserProfile } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

function VerifyEmail() {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [autoSendAttempted, setAutoSendAttempted] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  
  const { currentUser, updateVerificationStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get email and redirect from query params
  const params = new URLSearchParams(location.search);
  const emailParam = params.get('email');
  const tokenParam = params.get('token');
  const rawRedirectUrl = params.get('redirect') || '/';
  
  // Clean the redirect URL to prevent redirection loops and auth page redirects
  const redirectUrl = React.useMemo(() => {
    // Parse the URL to see if it's an internal URL
    let cleanUrl = rawRedirectUrl;
    let fragment = '';
    
    try {
      // If it's a full URL, extract just the path and fragment
      if (rawRedirectUrl.startsWith('http')) {
        const urlObj = new URL(rawRedirectUrl);
        cleanUrl = urlObj.pathname + urlObj.search;
        fragment = urlObj.hash;
      } else {
        // Handle local paths with fragments
        const hashIndex = cleanUrl.indexOf('#');
        if (hashIndex !== -1) {
          fragment = cleanUrl.substring(hashIndex);
          cleanUrl = cleanUrl.substring(0, hashIndex);
        }
      }
      
      // Don't redirect back to auth pages
      const authPages = ['/signin', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
      for (const authPage of authPages) {
        if (cleanUrl.startsWith(authPage)) {
          return '/'; // Redirect to home instead
        }
      }
      
      // Return the cleaned path with the fragment if it exists
      return cleanUrl + fragment;
    } catch (e) {
      console.error('Error parsing redirect URL:', e);
      return '/'; // Default to home on error
    }
  }, [rawRedirectUrl]);
  
  // First effect - immediately check verification status regardless of conditions
  useEffect(() => {
    const checkVerificationOnLoad = async () => {
      setIsCheckingStatus(true);
      console.log("Checking verification status on page load");
      
      // Try to get email from various sources
      const emailToCheck = emailParam || (currentUser && currentUser.email);
      
      if (!emailToCheck) {
        console.log("No email available to check");
        setIsCheckingStatus(false);
        return;
      }
      
      console.log("Email to check:", emailToCheck);
      
      // First try from currentUser if available
      if (currentUser) {
        console.log("Current user exists:", currentUser);
        if (currentUser.is_verified || currentUser.isVerified) {
          console.log("User is already verified according to auth context");
          setAlreadyVerified(true);
          setMessage('Your account is already verified. To change your email, please contact customer service.');
          setIsCheckingStatus(false);
          return;
        }
      } else {
        console.log("No current user in auth context");
      }
      
      try {
        // Try API call to check verification status
        console.log("Making API call to check verification status");
        const response = await checkVerificationStatus(emailToCheck);
        console.log("Verification status API response:", response);
        
        if (response && response.is_verified) {
          console.log("API confirms user is verified");
          setAlreadyVerified(true);
          setMessage('Your account is already verified. To change your email, please contact customer service.');
          
          // Update auth context
          if (currentUser && updateVerificationStatus) {
            updateVerificationStatus(true);
          }
        } else {
          console.log("API confirms user is NOT verified");
        }
      } catch (err) {
        console.error("Error checking verification status via API:", err);
        
        // If API check fails, try fetching user profile as fallback
        if (currentUser && currentUser.token) {
          try {
            console.log("Attempting to fetch user profile as fallback");
            const profile = await getUserProfile();
            console.log("User profile:", profile);
            
            if (profile && (profile.is_verified || profile.isVerified)) {
              console.log("Profile indicates user is verified");
              setAlreadyVerified(true);
              setMessage('Your account is already verified. To change your email, please contact customer service.');
              
              // Update auth context
              if (updateVerificationStatus) {
                updateVerificationStatus(true);
              }
            }
          } catch (profileErr) {
            console.error("Error fetching user profile:", profileErr);
          }
        }
      } finally {
        setIsCheckingStatus(false);
        setEmail(emailToCheck); // Set email input field
      }
    };
    
    checkVerificationOnLoad();
  }, [emailParam, currentUser, updateVerificationStatus]);
  
  // Process token if present in URL
  useEffect(() => {
    if (tokenParam && !alreadyVerified && !isCheckingStatus) {
      console.log("Processing token from URL:", tokenParam);
      setToken(tokenParam);
      handleVerifyToken(tokenParam);
    }
  }, [tokenParam, alreadyVerified, isCheckingStatus]);
  
  // Auto-send verification email effect
  useEffect(() => {
    const autoSendVerification = async () => {
      if (email && !autoSendAttempted && !tokenParam && !verificationSent && 
          !verificationSuccess && !alreadyVerified && !isCheckingStatus) {
        console.log("Auto-send verification is disabled - email already sent during registration");
        setAutoSendAttempted(true);
        setVerificationSent(true);
        setMessage(`A verification email has already been sent to ${email}. Please check your inbox or use the resend button if needed.`);
      }
    };
    
    autoSendVerification();
  }, [email, autoSendAttempted, tokenParam, verificationSent, verificationSuccess, alreadyVerified, isCheckingStatus]);
  
  const handleSendVerification = async () => {
    if (alreadyVerified) {
      setMessage('Your account is already verified. No need to verify again.');
      return;
    }
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await requestEmailVerification(email);
      
      setVerificationSent(true);
      setMessage(`Verification email sent to ${email}. Please check your inbox.`);
    } catch (err) {
      console.error('Error sending verification:', err);
      // Check if error indicates user is already verified
      if (err.status === 400 && err.detail?.includes('already verified')) {
        setAlreadyVerified(true);
        setMessage('This account is already verified. To change your email, please contact customer service.');
      } else {
        setError(err.detail || 'Failed to send verification email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyToken = async (tokenToVerify = token) => {
    if (alreadyVerified) {
      setMessage('Your account is already verified. No need to verify again.');
      return;
    }
    
    if (!tokenToVerify) {
      setError('Please enter the verification code');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await confirmEmail(tokenToVerify);
      
      // Check response for verification status
      if (response && (response.is_verified || response.verified)) {
        setAlreadyVerified(true);
      }
      
      setVerificationSuccess(true);
      setMessage('Email verified successfully!');
      
      // Redirect after successful verification
      setTimeout(() => {
        navigate(redirectUrl ? decodeURIComponent(redirectUrl) : '/profile');
      }, 2000);
    } catch (err) {
      console.error('Error verifying email:', err);
      // Check if error indicates user is already verified
      if (err.status === 400 && err.detail?.includes('already verified')) {
        setAlreadyVerified(true);
        setMessage('This account is already verified. To change your email, please contact customer service.');
      } else {
        setError(err.detail || 'Invalid or expired verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Extract JWT from URL if present (some email clients might append it as a fragment)
  useEffect(() => {
    const checkUrlForToken = () => {
      const hash = window.location.hash;
      if (hash && hash.startsWith('#token=')) {
        const tokenFromHash = hash.substring(7);
        if (tokenFromHash && !alreadyVerified) {
          setToken(tokenFromHash);
          handleVerifyToken(tokenFromHash);
        }
      }
    };
    
    checkUrlForToken();
  }, [alreadyVerified]);

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Verify Your Email</h2>
        
        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}
        
        {isCheckingStatus ? (
          <div className="verification-loading">
            <p>Checking verification status...</p>
          </div>
        ) : alreadyVerified ? (
          <div className="already-verified">
            <p>
              Your account is already verified. If you need to change your email address,
              please contact our customer service team for assistance.
            </p>
            
            <div className="auth-links">
              <Link to={redirectUrl ? decodeURIComponent(redirectUrl) : '/profile'}>
                Return to your previous page
              </Link>
            </div>
          </div>
        ) : !verificationSuccess && (
          <>
            {!verificationSent ? (
              <div className="verification-request">
                <p>
                  Please verify your email address to access all features.
                  We'll send a verification link to your email.
                </p>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                
                <button 
                  onClick={handleSendVerification}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Verification Email'}
                </button>
              </div>
            ) : (
              <div className="verification-code">
                <p>
                  We've sent a verification link to your email.
                  Please check your inbox and click the link to verify your account.
                </p>
                
                <p>Alternatively, you can enter the verification code here:</p>
                
                <div className="form-group">
                  {/* <label htmlFor="token">Verification Code</label> */}
                  {/* <input
                    id="token"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="Enter verification code"
                  /> */}
                </div>
                
                <button 
                  onClick={() => handleVerifyToken()}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                </button>
                
                <div className="resend-link">
                  <button 
                    onClick={handleSendVerification}
                    className="btn btn-link"
                    disabled={loading}
                  >
                    Resend verification email
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        
        <div className="auth-links">
          <Link to="/signin">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail; 